import React, { useState } from 'react';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';
import {
    Tabs, Tab, Box, Typography, Button,
    Dialog, DialogTitle, DialogContent, DialogActions,
    RadioGroup, FormControlLabel, Radio, FormControl, FormLabel,
    CircularProgress, Divider, Card
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SyncStatusBadge from './SyncStatusBadge';

const ADL_FIELDS = [
    { key: 'Grooming', label: 'Grooming' },
    { key: 'DressUpperBody', label: 'Dress Upper Body' },
    { key: 'DressLowerBody', label: 'Dress Lower Body' },
    { key: 'Bathing', label: 'Bathing' },
    { key: 'ToiletTransferring', label: 'Toilet Transferring' },
    { key: 'ToiletHygiene', label: 'Toilet Hygiene' },
    { key: 'Transferring', label: 'Transferring' },
    { key: 'Ambulation', label: 'Ambulation' },
];

const IADL_FIELDS = [
    { key: 'MoneyManagement', label: 'Money Management' },
    { key: 'HouseholdManagement', label: 'Household Management' },
    { key: 'HealthManagement', label: 'Health Management' },
    { key: 'MealPreparation', label: 'Meal Preparation' },
    { key: 'Communication', label: 'Communication' },
    { key: 'Transportation', label: 'Transportation' },
    { key: 'Shopping', label: 'Shopping' },
];

const ALL_ADL_FIELDS = [...ADL_FIELDS, ...IADL_FIELDS];

function InfoRow({ label, value }) {
    return (
        <Box style={{ display: 'flex', marginBottom: 6 }}>
            <Typography
                variant="body2"
                color="textSecondary"
                style={{ width: 175, flexShrink: 0, paddingRight: 8 }}
            >
                {label}
            </Typography>
            <Typography variant="body2" style={{ flex: 1 }}>{value || '—'}</Typography>
        </Box>
    );
}

function TabPanel({ children, value, index }) {
    return value === index ? <Box style={{ padding: 12, overflowY: 'auto', flex: 1 }}>{children}</Box> : null;
}

function YesNoRadioField({ label, fieldKey, value, onChange }) {
    return (
        <div style={{ marginBottom: 12 }}>
            <FormControl component="fieldset">
                <FormLabel component="legend" style={{ fontSize: 13, marginBottom: 2 }}>{label}</FormLabel>
                <RadioGroup
                    row
                    value={value || ''}
                    onChange={e => onChange(fieldKey, e.target.value)}
                >
                    <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                </RadioGroup>
            </FormControl>
            <Divider />
        </div>
    );
}

function PatientDetailPanel({ cust_details, extensionData, adlData, customer_id, addedBy, onADLSave }) {
    const [activeTab, setActiveTab] = useState(0);
    const [adlDialogOpen, setAdlDialogOpen] = useState(false);
    const [iadlDialogOpen, setIadlDialogOpen] = useState(false);
    const [adlForm, setAdlForm] = useState({});
    const [saving, setSaving] = useState(false);

    const openDialog = (type) => {
        const initial = {};
        ALL_ADL_FIELDS.forEach(f => { initial[f.key] = adlData?.[f.key] || ''; });
        setAdlForm(initial);
        if (type === 'adl') setAdlDialogOpen(true);
        else setIadlDialogOpen(true);
    };

    const handleChange = (key, value) => {
        setAdlForm(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = { customer_id, addedBy, ...adlForm };
            if (adlData?.customer_adl_id) {
                await axios.put(APIURL + `/customer_adl/${customer_id}`, payload);
                onADLSave({ ...adlData, ...adlForm });
            } else {
                await axios.post(APIURL + `/customer_adl`, payload);
                onADLSave({ customer_id, ...adlForm });
            }
            setAdlDialogOpen(false);
            setIadlDialogOpen(false);
        } catch (err) {
            console.error(err);
        }
        setSaving(false);
    };

    const c = cust_details || {};

    return (
        <Card elevation={3} style={{ height: '100%', borderRadius: '10px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box style={{ display: 'flex', alignItems: 'center', paddingLeft: 8, paddingRight: 8, borderBottom: '1px solid #e0e0e0' }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    variant="fullWidth"
                    style={{ flex: 1 }}
                >
                    <Tab label="PERSONAL" value={0} />
                    <Tab label="SOCIAL" value={1} />
                    <Tab label="ADL" value={2} />
                    <Tab label="IADL" value={3} />
                </Tabs>
                <SyncStatusBadge customerId={customer_id} />
            </Box>

            <TabPanel value={activeTab} index={0}>
                <InfoRow label="Name" value={c.cus_name} />
                <InfoRow label="Date of Birth" value={c.dob ? moment(c.dob).format('MM/DD/YYYY') : null} />
                <InfoRow label="Gender" value={c.gender} />
                <InfoRow label="Address" value={c.cus_address} />
                <InfoRow label="Phone" value={c.cus_phone || c.home_phone} />
                <InfoRow label="Mobile" value={c.mobile} />
                <InfoRow label="Email" value={c.cus_email} />
                <InfoRow label="Medicaid ID" value={c.medicaid_id} />
                <InfoRow label="DDD ID" value={c.DDDID} />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
                <InfoRow label="Race" value={c.Race || extensionData?.race} />
                <InfoRow label="Ethnicity" value={c.Ethnicity || extensionData?.ethnicity} />
                <InfoRow label="Preferred Language" value={extensionData?.preferred_language} />
                <InfoRow label="Marital Status" value={extensionData?.marital_status} />
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
                <div style={{ position: 'relative' }}>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => openDialog('adl')} style={{ position: 'absolute', top: 0, right: 0 }}>
                        Edit ADL
                    </Button>
                    {ADL_FIELDS.map(f => (
                        <InfoRow key={f.key} label={f.label} value={adlData?.[f.key]} />
                    ))}
                </div>
            </TabPanel>

            <TabPanel value={activeTab} index={3}>
                <div style={{ position: 'relative' }}>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => openDialog('iadl')} style={{ position: 'absolute', top: 0, right: 0 }}>
                        Edit IADL
                    </Button>
                    {IADL_FIELDS.map(f => (
                        <InfoRow key={f.key} label={f.label} value={adlData?.[f.key]} />
                    ))}
                </div>
            </TabPanel>

            {/* ADL Edit Dialog */}
            <Dialog open={adlDialogOpen} onClose={() => setAdlDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Edit ADL</DialogTitle>
                <DialogContent>
                    {ADL_FIELDS.map(f => (
                        <YesNoRadioField
                            key={f.key}
                            label={f.label}
                            fieldKey={f.key}
                            value={adlForm[f.key]}
                            onChange={handleChange}
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAdlDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* IADL Edit Dialog */}
            <Dialog open={iadlDialogOpen} onClose={() => setIadlDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Edit IADL</DialogTitle>
                <DialogContent>
                    {IADL_FIELDS.map(f => (
                        <YesNoRadioField
                            key={f.key}
                            label={f.label}
                            fieldKey={f.key}
                            value={adlForm[f.key]}
                            onChange={handleChange}
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIadlDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}

export default PatientDetailPanel;
